import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import type { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import { PERMISSION, RegistrationState } from '@/models';
import {
  AppFunctionService,
  AuthService,
  DataService,
  LanguageService,
  NnnRegistrationService,
  NotificationService,
} from '@/services';
import type {
  NnnRegistrationInstrument,
  NnnRegistrationIntervention,
  NnnRegistrationResult,
  NnnRegistrationRow,
  NnnRegistrationRowKenmerk,
} from '@/api';
import type { NicInterventieSummary, NnnRegistrationWithRows, NocZorgresultaatSummary } from '@/services';
import { SpinnerButtonConfig } from '@/ui/spinner-button/spinner-button-config';
import { formatTime } from '@/utils';
import type {
  AddNnnRegistrationRowDialogData,
  AddNnnRegistrationRowDialogResult,
  NandaDiagnoseView,
  NnnInstrumentOptionGroup,
  NnnNotesField,
  NnnRowField,
  SaveState,
} from '../../types/careplan-nnn.types';
import { copyRowForImport } from '../../utils/copy-row-for-import';
import { sortRowsByOrder } from '../../utils/sort-rows-by-order';
import { withTimeHHmm } from '../../utils/with-time-hh-mm';
import { AddNnnRegistrationRowDialogComponent } from '../add-nnn-registration-row-dialog/add-nnn-registration-row-dialog.component';
import { NnnDiagnosisSectionComponent } from '../nnn-diagnosis-section/nnn-diagnosis-section.component';
import { NnnEvaluationDateCellComponent } from '../nnn-evaluation-date-cell/nnn-evaluation-date-cell.component';
import {
  NEW_REGISTRATION_VERSION,
  NnnRegistrationVersionSelectComponent,
} from '../nnn-registration-version-select/nnn-registration-version-select.component';
import { NnnInstrumentsSectionComponent } from '../nnn-instruments-section/nnn-instruments-section.component';
import { NnnInterventionsSectionComponent } from '../nnn-interventions-section/nnn-interventions-section.component';
import { NnnResultsSectionComponent } from '../nnn-results-section/nnn-results-section.component';
import { NnnViewUserFieldsComponent } from '../nnn-view-user-fields/nnn-view-user-fields.component';

// Every edit saves the whole row: v2 has no per-property endpoint.
@Component({
  selector: 'app-nnn-registration-row-table',
  standalone: true,
  imports: [
    SharedModule,
    NnnRegistrationVersionSelectComponent,
    NnnViewUserFieldsComponent,
    NnnDiagnosisSectionComponent,
    NnnEvaluationDateCellComponent,
    NnnResultsSectionComponent,
    NnnInterventionsSectionComponent,
    NnnInstrumentsSectionComponent,
  ],
  templateUrl: './nnn-registration-row-table.component.html',
  styleUrl: './nnn-registration-row-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnRegistrationRowTableComponent implements OnInit {
  private readonly registrationService = inject(NnnRegistrationService);
  private readonly appFunctionService = inject(AppFunctionService);
  private readonly authService = inject(AuthService);
  private readonly dataService = inject(DataService);
  private readonly notificationService = inject(NotificationService);
  private readonly languageService = inject(LanguageService);
  private readonly dialog = inject(MatDialog);

  readonly patientId = input.required<string>();
  readonly registrations = input<NnnRegistrationWithRows[]>([]);
  readonly lastFinalRegistration = input<NnnRegistrationWithRows | null>(null);
  readonly state = input.required<RegistrationState>();
  readonly editOptions = input(false);
  readonly nandaIndex = input<Map<string, NandaDiagnoseView>>(new Map());
  readonly nocIndex = input<Map<string, NocZorgresultaatSummary>>(new Map());
  readonly nicIndex = input<Map<string, NicInterventieSummary>>(new Map());
  readonly appFunctionGroups = input<NnnInstrumentOptionGroup[]>([]);
  readonly expandAllOnLoad = input(false);

  readonly makeFinal = output<NnnRegistrationWithRows>();

  protected readonly conceptState = RegistrationState.CONCEPT;

  protected readonly writeEnabled = signal(false);
  protected readonly deleteEnabled = signal(false);
  protected readonly isUserTeacher =
    this.authService.isUserTeacher() || this.authService.isUserAdmin() || this.authService.isUserSuperAdmin();

  protected readonly loading = signal(false);

  protected readonly registrationList = linkedSignal(() => this.registrations() ?? []);
  protected readonly selectedVersionId = linkedSignal(() => this.registrationList()[0]?.id ?? NEW_REGISTRATION_VERSION);
  protected readonly activeRegistration = computed(
    () => this.registrationList().find(r => r.id === this.selectedVersionId()) ?? null
  );
  protected readonly activeRows = linkedSignal(() => {
    const registration = this.activeRegistration();
    return registration ? sortRowsByOrder(registration.rows) : [];
  });
  protected readonly rowsEditable = computed(() => this.state() === RegistrationState.CONCEPT && this.writeEnabled());
  protected readonly rowViews = computed(() => {
    const noDiagnosisLabel = this.languageService.translateWithCap('epd.functions.careplan-nnn.row-table.no-diagnosis');
    const diagnosisLabel = this.languageService.translateWithCap(
      'epd.functions.careplan-nnn.row-table.columns.diagnosis'
    );
    return this.activeRows().map(row => {
      const diagnosis = row.nnnDiagnosisDbRef ? this.nandaIndex().get(row.nnnDiagnosisDbRef) : undefined;
      return {
        row,
        diagnose: diagnosis ?? null,
        diagnosisCode: diagnosis?.code ?? null,
        diagnosisName: diagnosis?.omschrijving ?? (row.nnnDiagnosisDbRef || noDiagnosisLabel),
        classification: diagnosis
          ? [diagnosis.domeinNaam, diagnosis.klasseNaam].filter(Boolean).join(' · ')
          : diagnosisLabel,
      };
    });
  });
  private readonly saveStates = signal<Record<string, SaveState>>({});
  private readonly expandedRowIds = linkedSignal<readonly NnnRegistrationRow[], ReadonlySet<string>>({
    source: this.activeRows,
    computation: (rows, previous) =>
      this.expandAllOnLoad() ? new Set(rows.map(row => row.id)) : (previous?.value ?? new Set()),
  });

  protected readonly viewDate = linkedSignal<Date | null>(() => {
    const registration = this.activeRegistration();
    return registration?.viewRegisteredDate ? new Date(registration.viewRegisteredDate) : null;
  });
  protected readonly viewTime = linkedSignal(() => {
    const registration = this.activeRegistration();
    return registration?.viewRegisteredDate ? formatTime(new Date(registration.viewRegisteredDate)) : '';
  });
  protected readonly viewUser = linkedSignal(() => this.activeRegistration()?.viewRegisteredUser ?? '');

  protected readonly confirmFinalOpen = signal(false);
  protected triggerOrigin!: CdkOverlayOrigin;
  protected readonly confirmFinalText = this.languageService.translateWithCap('general.actions.are-you-sure');

  protected readonly makeFinalBtnConfig: SpinnerButtonConfig = {
    text: this.languageService.translateWithCap('epd.functions.careplan.row-table.make-final'),
  };
  private readonly addRowBtnBaseConfig: SpinnerButtonConfig = {
    ...new SpinnerButtonConfig(),
    text: this.languageService.translateWithCap('epd.functions.careplan.row-table.add-row'),
    size: 'small',
  };
  private readonly addRowLoading = signal(false);
  protected readonly addRowBtnConfig = computed<SpinnerButtonConfig>(() => ({
    ...this.addRowBtnBaseConfig,
    loading: this.addRowLoading(),
  }));
  protected readonly deleteBtnConfig: SpinnerButtonConfig = {
    text: this.languageService.translateWithCap('general.actions.delete'),
    type: 'stroked',
    backgroundColor: '#F44336',
    textColor: '#F44336',
  };
  protected readonly deleteRowBtnConfig: SpinnerButtonConfig = {
    type: 'stroked',
    backgroundColor: '#F44336',
    textColor: '#F44336',
    size: 'small',
  };
  protected readonly previousRegistrationBtnConfig: SpinnerButtonConfig = {
    text: this.languageService.translateWithCap('epd.functions.general.import-previous-registration'),
    backgroundColor: 'white',
    textColor: '#5C739D',
    type: 'flat',
  };

  ngOnInit(): void {
    this.writeEnabled.set(this.appFunctionService.checkAppFunctionPermission(PERMISSION.WRITE));
    this.deleteEnabled.set(this.appFunctionService.checkAppFunctionPermission(PERMISSION.DELETE));
  }

  protected readonly allExpanded = computed(() => {
    const rows = this.activeRows();
    return rows.length > 0 && rows.every(row => this.expandedRowIds().has(row.id));
  });

  protected toggleAllRows(): void {
    this.expandedRowIds.set(this.allExpanded() ? new Set() : new Set(this.activeRows().map(row => row.id)));
  }

  protected isExpanded(rowId: string): boolean {
    return this.expandedRowIds().has(rowId);
  }

  protected setRowExpanded(rowId: string, expanded: boolean): void {
    this.expandedRowIds.update(ids => {
      const next = new Set(ids);
      if (expanded) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  }

  private expandRow(rowId: string): void {
    this.expandedRowIds.update(ids => new Set(ids).add(rowId));
  }

  protected saveState(rowId: string, field: NnnRowField): SaveState {
    return this.saveStates()[`${rowId}:${field}`] ?? '';
  }

  private setSaveState(rowId: string, field: NnnRowField, state: SaveState): void {
    this.saveStates.update(states => ({ ...states, [`${rowId}:${field}`]: state }));
  }

  protected onVersionChange(id: string): void {
    this.selectedVersionId.set(id);
  }

  protected onNotesInput(row: NnnRegistrationRow, field: NnnNotesField, value: string): void {
    row[field] = value;
  }

  protected async saveNotes(row: NnnRegistrationRow, field: NnnNotesField): Promise<void> {
    await this.saveRow(row, field);
  }

  protected async saveEvaluationDate(row: NnnRegistrationRow, value: Date | null): Promise<void> {
    row.evaluationDate = value ? value.getTime() : null;
    await this.saveRow(row, 'evaluationDate');
  }

  protected async onResultsChange(row: NnnRegistrationRow, results: NnnRegistrationResult[]): Promise<void> {
    row.results = results;
    await this.saveRow(row, 'results');
  }

  protected async onInterventionsChange(
    row: NnnRegistrationRow,
    interventions: NnnRegistrationIntervention[]
  ): Promise<void> {
    row.interventions = interventions;
    await this.saveRow(row, 'interventions');
  }

  protected async onKenmerkenChange(row: NnnRegistrationRow, kenmerken: NnnRegistrationRowKenmerk[]): Promise<void> {
    const previous = row.kenmerken;
    row.kenmerken = kenmerken;
    const saved = await this.saveRow(row, 'kenmerken');
    if (!saved) {
      row.kenmerken = previous;
      this.activeRows.update(rows => [...rows]);
    }
  }

  protected async onInstrumentsChange(
    row: NnnRegistrationRow,
    instruments: NnnRegistrationInstrument[]
  ): Promise<void> {
    row.instruments = instruments;
    await this.saveRow(row, 'instruments');
  }

  private async saveRow(row: NnnRegistrationRow, field: NnnRowField): Promise<boolean> {
    const registration = this.activeRegistration();
    if (!registration) {
      return false;
    }
    this.setSaveState(row.id, field, 'saving');
    const { error } = await this.registrationService.updateRowProperties(this.patientId(), registration.id, row);
    if (error) {
      this.setSaveState(row.id, field, '');
      return false;
    }
    this.setSaveState(row.id, field, 'saved');
    setTimeout(() => this.setSaveState(row.id, field, ''), 3000);
    return true;
  }

  protected async openAddRowModal(): Promise<void> {
    this.addRowLoading.set(true);
    const data: AddNnnRegistrationRowDialogData = { patientId: this.patientId() };
    const dialogRef = this.dialog.open(AddNnnRegistrationRowDialogComponent, {
      width: '95vw',
      maxWidth: '1400px',
      panelClass: ['card-primary', 'add-nnn-row-dialog-panel'],
      data,
    });
    const chosen: AddNnnRegistrationRowDialogResult | undefined = await firstValueFrom(dialogRef.afterClosed());
    if (!chosen) {
      this.addRowLoading.set(false);
      return;
    }
    this.loading.set(true);
    const registration = this.activeRegistration() ?? (await this.createRegistration());
    if (registration) {
      await this.addRow(registration, chosen);
    }
    this.addRowLoading.set(false);
    this.loading.set(false);
  }

  private async createRegistration(): Promise<NnnRegistrationWithRows | null> {
    const { data } = await this.registrationService.createRegistration(this.patientId());
    if (!data) {
      return null;
    }
    const withRows: NnnRegistrationWithRows = { ...data, rows: [] };
    this.dataService.updateSideMenu();
    this.registrationList.update(list => [withRows, ...list]);
    this.selectedVersionId.set(withRows.id);
    return withRows;
  }

  private async addRow(
    registration: NnnRegistrationWithRows,
    chosen: AddNnnRegistrationRowDialogResult
  ): Promise<void> {
    const diagnose = chosen.diagnose;
    const created = await this.registrationService.createRegistrationRow(
      this.patientId(),
      registration.id,
      diagnose.klasse != null ? String(diagnose.klasse) : null
    );
    if (!created.data) {
      return;
    }
    const row: NnnRegistrationRow = {
      ...created.data,
      nnnDiagnosisDbRef: diagnose.id,
      diagnosisNotes: null,
      kenmerken: chosen.kenmerken,
    };
    const { error } = await this.registrationService.updateRowProperties(this.patientId(), registration.id, row);
    if (error) {
      return;
    }
    registration.rows.push(row);
    this.activeRows.update(rows => [...rows, row]);
    this.expandRow(row.id);
  }

  protected async removeRow(row: NnnRegistrationRow): Promise<void> {
    const registration = this.activeRegistration();
    if (!registration) {
      return;
    }
    this.deleteRowBtnConfig.loading = true;
    const { error } = await this.registrationService.deleteRegistrationRow(this.patientId(), registration.id, row.id);
    this.deleteRowBtnConfig.loading = false;
    if (error) {
      return;
    }
    registration.rows = registration.rows.filter(r => r.id !== row.id);
    this.activeRows.update(rows => rows.filter(r => r.id !== row.id));
    this.notificationService.success(
      this.languageService.translateWithCap('epd.functions.careplan.row-table.notifications.row-deleted')
    );
  }

  protected async deleteRegistration(): Promise<void> {
    const registration = this.activeRegistration();
    if (!registration) {
      return;
    }
    this.deleteBtnConfig.loading = true;
    const { error } = await this.registrationService.deleteRegistration(this.patientId(), registration.id);
    this.deleteBtnConfig.loading = false;
    if (error) {
      return;
    }
    this.dataService.updateSideMenu();
    this.notificationService.success(
      this.languageService.translateWithCap('epd.functions.careplan.row-table.notifications.registration-deleted')
    );
    this.registrationList.update(list => list.filter(r => r.id !== registration.id));
  }

  protected async importPreviousRegistration(): Promise<void> {
    const lastFinal = this.lastFinalRegistration();
    if (!lastFinal) {
      return;
    }
    this.previousRegistrationBtnConfig.loading = true;
    const registration = this.activeRegistration() ?? (await this.createRegistration());
    if (!registration) {
      this.previousRegistrationBtnConfig.loading = false;
      return;
    }
    const existingDiagnoses = new Set(this.activeRows().map(r => r.nnnDiagnosisDbRef));
    for (const oldRow of sortRowsByOrder(lastFinal.rows)) {
      if (oldRow.nnnDiagnosisDbRef && existingDiagnoses.has(oldRow.nnnDiagnosisDbRef)) {
        continue;
      }
      const created = await this.registrationService.createRegistrationRow(
        this.patientId(),
        registration.id,
        oldRow.nnnClassDbRef,
        oldRow.id
      );
      if (!created.data) {
        continue;
      }
      const copy = copyRowForImport(oldRow, created.data);
      const { error } = await this.registrationService.updateRowProperties(this.patientId(), registration.id, copy);
      if (error) {
        continue;
      }
      registration.rows.push(copy);
      this.activeRows.update(rows => [...rows, copy]);
    }
    this.previousRegistrationBtnConfig.loading = false;
  }

  protected toggleConfirmFinal(trigger: CdkOverlayOrigin): void {
    this.triggerOrigin = trigger;
    this.confirmFinalOpen.update(open => !open);
  }

  protected onConfirmFinalClose(confirmed: boolean): void {
    setTimeout(() => this.confirmFinalOpen.set(false), 50);
    if (confirmed) {
      this.makeFinalClick();
    }
  }

  private makeFinalClick(): void {
    const registration = this.activeRegistration();
    const rows = this.activeRows();
    const valid = rows.length > 0 && rows.every(row => row.nnnDiagnosisDbRef && row.evaluationDate);
    if (!registration || !valid) {
      this.notificationService.warning(
        '',
        this.languageService.translateWithCap(
          'epd.functions.careplan-nnn.row-table.notifications.diagnosis-and-date-required'
        )
      );
      return;
    }
    this.makeFinal.emit(registration);
  }

  protected onViewDateChange(value: Date | null): void {
    this.viewDate.set(value);
    void this.saveViewUser();
  }

  protected onViewTimeChange(time: string): void {
    this.viewTime.set(time);
    void this.saveViewUser();
  }

  protected onViewUserChange(user: string): void {
    this.viewUser.set(user);
    void this.saveViewUser();
  }

  private async saveViewUser(): Promise<void> {
    const registration = this.activeRegistration();
    const date = this.viewDate();
    if (!registration || !date) {
      return;
    }
    const dateTime = withTimeHHmm(date, this.viewTime());
    const { error } = await this.registrationService.updateViewUser(
      this.patientId(),
      registration.id,
      this.viewUser(),
      dateTime.getTime()
    );
    if (!error) {
      registration.viewRegisteredDate = dateTime.getTime();
      registration.viewRegisteredUser = this.viewUser();
    }
  }
}
